import type { AttachmentCards } from 'types/Attachments';
import type { TranslationImageField } from 'types/AiHubTranslation';

type PlacedTranslationImage = TranslationImageField & { cardIndex: number };

type ImageVersion = 'translated' | 'original';

const isLibraryImage = (image: TranslationImageField) => !!image.assetId;

export function isTranslatedImageReady(
  image: TranslationImageField,
): image is PlacedTranslationImage {
  const hasTranslatedId = isLibraryImage(image)
    ? !!image.translatedAssetId
    : image.translatedMediumId != null;

  return (
    image.status === 'completed' &&
    !!image.translatedUrl &&
    image.cardIndex != null &&
    hasTranslatedId
  );
}

function placeImage(
  card: AttachmentCards[number] | undefined,
  image: TranslationImageField,
  version: ImageVersion,
): AttachmentCards[number] | undefined {
  if (!card) return card;

  if (isLibraryImage(image)) {
    const id =
      version === 'translated' ? image.translatedAssetId : image.assetId;
    if (!id || card.asset?.type !== 'image') return card;
    return { ...card, asset: { ...card.asset, id } };
  }

  const id =
    version === 'translated' ? image.translatedMediumId : image.mediumId;
  if (id == null || card.medium?.type !== 'image') return card;
  return { ...card, medium: { ...card.medium, id } };
}

function placeImages(
  cards: AttachmentCards,
  images: PlacedTranslationImage[],
  version: ImageVersion,
): AttachmentCards {
  const result = [...cards];

  for (const image of images) {
    const placed = placeImage(result[image.cardIndex], image, version);
    if (placed) result[image.cardIndex] = placed;
  }

  return result;
}

export function applyTranslatedImagesToCards(
  cards: AttachmentCards,
  images: TranslationImageField[],
): AttachmentCards {
  return placeImages(
    cards,
    images.filter(isTranslatedImageReady),
    'translated',
  );
}

export function revertTranslatedImagesOnCards(
  cards: AttachmentCards,
  images: TranslationImageField[],
): AttachmentCards {
  return placeImages(
    cards,
    images.filter(
      (image): image is PlacedTranslationImage => image.cardIndex != null,
    ),
    'original',
  );
}

export function areTranslatedImagesApplied(
  cards: AttachmentCards,
  images: TranslationImageField[],
): boolean {
  const translated = images.filter(isTranslatedImageReady);

  return (
    translated.length > 0 &&
    translated.every((image) => {
      const card = cards[image.cardIndex];
      return isLibraryImage(image)
        ? card?.asset?.id === image.translatedAssetId
        : card?.medium?.id === image.translatedMediumId;
    })
  );
}

export function hasTranslatableImageCard(cards: AttachmentCards): boolean {
  return cards.some(
    (card) =>
      card.medium?.type === 'image' ||
      (card.asset?.type === 'image' && !!card.asset.url),
  );
}
